import type { DSPropTypesSchema, GlobalAttributesT, XstyledProps } from '@elliemae/ds-props-helpers';
import {
  PropTypes,
  getPropsPerSlotPropTypes,
  globalAttributesPropTypes,
  xstyledPropTypes,
} from '@elliemae/ds-props-helpers';
import type { TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import type { DSTabsInternalsT } from './sharedTypes.js';

import { DSTabsName, TABS_SLOTS, TAB_TYPES, TAB_TYPES_VALUES_AS_ARRAY } from './constants/index.js';

const noop = (): void => {};

export declare namespace DSTabT {
  export interface DefaultProps {
    tabId: string;
    applyAriaDisabled: boolean;
  }

  export interface OptionalProps {
    style?: Record<string, unknown>;
    title?: string;
    required?: boolean;
    disabled?: boolean;
    ref?: React.MutableRefObject<HTMLButtonElement>;
    onClick?: (tabId: string | number, e: React.MouseEvent) => null | void;
    onKeyDown?: (e: React.KeyboardEvent) => null | void;
  }

  export interface RequiredProps {
    children: TypescriptHelpersT.ReactChildrenComplete;
  }

  export interface Props extends Partial<DefaultProps>, OptionalProps, RequiredProps {}
}

export declare namespace DSTabsT {
  export interface DefaultProps {
    containerProps: Record<string, unknown>;
    animated: boolean;
    enableMouseEvents: boolean;
    allowTextSelection: boolean;
    onTabChange: (tabId: string, e?: React.MouseEvent) => void;
    tabsListAriaLabel: string;
    type: DSTabsInternalsT.TabTypesT;
    children: React.ReactElement<DSTabT.Props>[] | readonly React.ReactElement<DSTabT.Props>[];
    onlyRenderActiveTab: boolean;
    fixedTabsHeaders: boolean;
    withCarousel: boolean;
    isDSMobile: boolean;
    showSelectionIndicator: boolean;
    showSeparator: boolean;
    tabPanelsProps: GlobalAttributesT<HTMLDivElement>;
    swipeableViewsContainerProps: object;
  }

  export interface OptionalProps {
    firstSubtabRef?: React.MutableRefObject<HTMLElement>;
    lastTabRef?: React.MutableRefObject<HTMLElement>;
    TabBarExtraContent?: React.ComponentType;
    tabBarExtraContent?: React.ReactNode;
    activeTab?: string;
    innerRef?: React.MutableRefObject<HTMLDivElement>;
  }

  export interface Props
    extends Partial<DefaultProps>,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLElement>, keyof DefaultProps | keyof XstyledProps>,
      XstyledProps {}

  export interface InternalProps
    extends DefaultProps,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLElement>, keyof DefaultProps | keyof XstyledProps>,
      XstyledProps {}
}

export const DSTabPropTypes: DSPropTypesSchema<DSTabT.Props> = {
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,
  tabId: PropTypes.string.description('Unique Tab id.').defaultValue(''),
  title: PropTypes.string.description('Tab title.').defaultValue(''),
  required: PropTypes.bool.description('Show a required mark next to the title.').defaultValue(false),
  disabled: PropTypes.bool.description('Disables Tab.').defaultValue(false),
  applyAriaDisabled: PropTypes.bool
    .description(
      'Whether to apply disabled styling and announce as disabled in SR. CAN STILL RECEIVE FOCUS. PREVENTS ONLY MAIN ACTION.',
    )
    .defaultValue(false),
  style: PropTypes.object.description('Custom styles for the tab.').defaultValue({}),
  ref: PropTypes.shape({ current: PropTypes.any }).description('Ref to the tab element.').defaultValue(undefined),
  onClick: PropTypes.func.description('Click handler for the tab.').defaultValue(undefined),
  onKeyDown: PropTypes.func.description('Keydown handler for the tab.').defaultValue(undefined),
  children: PropTypes.node.description('Content of the tab.').isRequired,
};

export const DSTabsPropTypes: DSPropTypesSchema<DSTabsT.Props> = {
  ...getPropsPerSlotPropTypes(DSTabsName, TABS_SLOTS),
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,
  animated: PropTypes.bool.description('Whether the Tab transition should animate or not.').defaultValue(true),
  enableMouseEvents: PropTypes.bool
    .description('Enables Tab transition with mouse drag events.')
    .defaultValue(false)
    .deprecated({ version: '3.55.x', message: 'This prop is no longer supported.' }),
  allowTextSelection: PropTypes.bool
    .description('Allows text selection when mouse drag events are active.')
    .defaultValue(false),
  onTabChange: PropTypes.func.description('Handler when a user clicks on a different Tab.').defaultValue('() => {}'),
  type: PropTypes.oneOf(TAB_TYPES_VALUES_AS_ARRAY).description('Tab type.').defaultValue(TAB_TYPES_VALUES_AS_ARRAY[0]),
  fixedTabsHeaders: PropTypes.bool.description('Fixes the tab headers.').defaultValue(false),
  isDSMobile: PropTypes.bool.description('Indicates if the component is in mobile mode.').defaultValue(false),
  showSelectionIndicator: PropTypes.bool
    .description('Shows the selection indicator for the active tab.')
    .defaultValue(true),
  showSeparator: PropTypes.bool.description('Shows a separator between tabs.').defaultValue(true),

  onlyRenderActiveTab: PropTypes.bool.description('Only renders the active Tab in the DOM.').defaultValue(false),
  withCarousel: PropTypes.bool.description('Activate carousel behavior.').defaultValue(false),
  activeTab: PropTypes.string
    .description('Passing a value makes DSTabs a controlled component.')
    .defaultValue('First DSTab children.'),
  children: PropTypes.node.description('Use DSTab component as children.').isRequired,
  tabsListAriaLabel: PropTypes.string.description('Aria label for the list.').defaultValue('Tab list.'),
  containerProps: PropTypes.object.description('Additional properties for the container.').defaultValue({}),
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
    .description('Ref to the container.')
    .defaultValue(undefined),
  tabPanelsProps: PropTypes.shape(globalAttributesPropTypes)
    .description('Additional properties for the tab container.')
    .defaultValue({}),
  swipeableViewsContainerProps: PropTypes.object
    .description('Additional properties for the swipeable container.')
    .defaultValue(undefined)
    .deprecated({ version: '3.55.x', message: 'This prop is no longer supported.' }),
  firstSubtabRef: PropTypes.shape({ current: PropTypes.any })
    .description('Ref to the first subtab element.')
    .defaultValue(undefined),
  lastTabRef: PropTypes.shape({ current: PropTypes.any })
    .description('Ref to the last tab element.')
    .defaultValue(undefined),
  TabBarExtraContent: PropTypes.element.description('Additional content to display in the tab bar.'),
  tabBarExtraContent: PropTypes.node
    .description('Additional content to display in the tab bar.')
    .defaultValue(undefined),
};

export const DSTabsDefaultProps: DSTabsT.DefaultProps = {
  containerProps: {},
  animated: true,
  enableMouseEvents: false,
  allowTextSelection: false,
  onTabChange: noop,
  tabsListAriaLabel: 'Tab list',
  type: TAB_TYPES.NORMAL,
  onlyRenderActiveTab: false,
  withCarousel: false,
  isDSMobile: false,
  showSelectionIndicator: true,
  children: [],
  fixedTabsHeaders: false,
  showSeparator: true,
  tabPanelsProps: {},
  swipeableViewsContainerProps: {},
};
